import { Injectable } from '@angular/core';
import { openBlobInNewTab, downloadBlob } from '../../../utils/blob-viewer.util';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';

import { MeetingNotesService } from '@services/meeting-notes.service';
import { ErrorDialogService } from '@services/error-dialog.service';
import { MeetingNotesActions } from '@store/actions/meeting-notes/meeting-notes.actions';
import { selectUserOrgId } from '@store/selectors/user/user.selectors';

@Injectable()
export class MeetingNotesEffects {

  loadMeetingNotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingNotesActions.loadMeetingNotes),
      withLatestFrom(this.store.select(selectUserOrgId)),
      switchMap(([, orgId]) =>
        this.meetingNotesService.getMeetingNotes(orgId).pipe(
          map((notes) => MeetingNotesActions.loadMeetingNotesSuccess({ notes })),
          catchError((error) =>
            of(MeetingNotesActions.loadMeetingNotesFailure({
              error: error.message || 'Failed to load meeting notes',
            }))
          )
        )
      )
    )
  );

  loadMeetingNotesFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingNotesActions.loadMeetingNotesFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to load meeting notes. Please try again.',
            'Meeting Notes Error'
          );
        })
      ),
    { dispatch: false }
  );

  uploadMeetingNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingNotesActions.uploadMeetingNote),
      withLatestFrom(this.store.select(selectUserOrgId)),
      switchMap(([{ file, meetingDescription, meetingDate, sendNotification }, orgId]) =>
        this.meetingNotesService.uploadMeetingNote({
          organizationId: orgId,
          meetingDescription,
          meetingDate,
          sendNotification,
          file,
        }).pipe(
          map((note) => MeetingNotesActions.uploadMeetingNoteSuccess({ note })),
          catchError((error) =>
            of(MeetingNotesActions.uploadMeetingNoteFailure({
              error: error.message || 'Failed to upload meeting note',
            }))
          )
        )
      )
    )
  );

  uploadMeetingNoteSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingNotesActions.uploadMeetingNoteSuccess),
      map(() => MeetingNotesActions.loadMeetingNotes())
    )
  );

  uploadMeetingNoteFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingNotesActions.uploadMeetingNoteFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to upload the meeting note. Please try again.',
            'Upload Error'
          );
        })
      ),
    { dispatch: false }
  );

  deleteMeetingNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingNotesActions.deleteMeetingNote),
      switchMap(({ id }) =>
        this.meetingNotesService.deleteMeetingNote(id).pipe(
          map(() => MeetingNotesActions.deleteMeetingNoteSuccess({ id })),
          catchError((error) =>
            of(MeetingNotesActions.deleteMeetingNoteFailure({
              error: error.message || 'Failed to delete meeting note',
            }))
          )
        )
      )
    )
  );

  deleteMeetingNoteFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingNotesActions.deleteMeetingNoteFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to delete the meeting note. Please try again.',
            'Delete Error'
          );
        })
      ),
    { dispatch: false }
  );

  viewMeetingNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingNotesActions.viewMeetingNote),
      withLatestFrom(this.store.select(selectUserOrgId)),
      switchMap(([{ id, fileName }, orgId]) =>
        this.meetingNotesService.viewMeetingNote(id, orgId).pipe(
          map((blob) => MeetingNotesActions.viewMeetingNoteSuccess({ blob, fileName })),
          catchError((error) =>
            of(MeetingNotesActions.viewMeetingNoteFailure({
              error: error.message || 'Failed to view meeting note',
            }))
          )
        )
      )
    )
  );

  viewMeetingNoteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingNotesActions.viewMeetingNoteSuccess),
        tap(({ blob }) => openBlobInNewTab(blob))
      ),
    { dispatch: false }
  );

  viewMeetingNoteFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingNotesActions.viewMeetingNoteFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to view the meeting note. Please try again.',
            'View Error'
          );
        })
      ),
    { dispatch: false }
  );

  downloadMeetingNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeetingNotesActions.downloadMeetingNote),
      withLatestFrom(this.store.select(selectUserOrgId)),
      switchMap(([{ id, fileName }, orgId]) =>
        this.meetingNotesService.viewMeetingNote(id, orgId).pipe(
          map((blob) => MeetingNotesActions.downloadMeetingNoteSuccess({ blob, fileName })),
          catchError((error) =>
            of(MeetingNotesActions.downloadMeetingNoteFailure({
              error: error.message || 'Failed to download meeting note',
            }))
          )
        )
      )
    )
  );

  downloadMeetingNoteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingNotesActions.downloadMeetingNoteSuccess),
        tap(({ blob, fileName }) => downloadBlob(blob, fileName))
      ),
    { dispatch: false }
  );

  downloadMeetingNoteFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MeetingNotesActions.downloadMeetingNoteFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to download the meeting note. Please try again.',
            'Download Error'
          );
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private meetingNotesService: MeetingNotesService,
    private errorDialogService: ErrorDialogService,
  ) {}
}
